import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { theme } from '../../theme';
import { apiRequest } from '../../utils/apiUtils';
import {
  ArrowLeft,
  MapPin,
  Calendar,
  Clock,
  User,
  CreditCard,
  CheckCircle2,
  Edit,
  XCircle,
  Plus,
} from 'lucide-react';
import { VenueBooking, Venue } from '../../types/venue';
import { formatPrice } from '../../utils/venueHelpers';

interface BookingDetail extends VenueBooking {
  venue: Venue;
  booker: {
    id: string;
    name: string;
    email: string;
  };
  mercadopagoPayment?: {
    status: string;
    paymentId?: string;
  };
}

const BookingDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [booking, setBooking] = useState<BookingDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;

    const fetchBooking = async () => {
      try {
        setLoading(true);
        const response = await apiRequest(`/api/bookings/${id}`);

        if (response.data) {
          setBooking(response.data);
        } else {
          setError('No se pudo cargar la reserva');
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error fetching booking:', err);
        setError('Error al cargar la reserva');
      } finally {
        setLoading(false);
      }
    };

    fetchBooking();
  }, [id]);

  const handleCancelBooking = async () => {
    if (!booking || !window.confirm('¿Estás seguro de que querés cancelar esta reserva?')) {
      return;
    }

    try {
      const response = await apiRequest(`/api/bookings/${booking.id}/cancel`, {
        method: 'PUT',
      });

      if (response.data) {
        setBooking({ ...booking, status: 'CANCELLED' });
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error canceling booking:', err);
      alert('Error al cancelar la reserva');
    }
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'CONFIRMED':
        return theme.colors.primary;
      case 'PENDING':
        return theme.colors.warning;
      case 'COMPLETED':
        return '#8BC34A';
      case 'CANCELLED':
        return theme.colors.danger;
      case 'NO_SHOW':
        return theme.colors.textSecondary;
      default:
        return theme.colors.textSecondary;
    }
  };

  const getStatusLabel = (status: string): string => {
    const labels: Record<string, string> = {
      PENDING: 'PENDIENTE',
      CONFIRMED: 'CONFIRMADA',
      COMPLETED: 'COMPLETADA',
      CANCELLED: 'CANCELADA',
      NO_SHOW: 'NO ASISTIÓ',
    };
    return labels[status] || status;
  };

  // Styles
  const containerStyle: React.CSSProperties = {
    minHeight: '100vh',
    backgroundColor: theme.colors.background,
  };

  const headerStyle: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    padding: '32px 48px',
    borderBottom: `1px solid ${theme.colors.border}`,
  };

  const backButtonStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: theme.colors.primary,
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    marginBottom: '16px',
  };

  const headerContentStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '36px',
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.primary,
    fontFamily: theme.fonts.heading,
    letterSpacing: theme.fonts.letterSpacingWide,
    marginBottom: '8px',
  };

  const subtitleStyle: React.CSSProperties = {
    fontSize: '14px',
    color: theme.colors.textSecondary,
  };

  const statusBadgeStyle = (status: string): React.CSSProperties => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '12px 24px',
    backgroundColor: getStatusColor(status),
    color: '#181A1B',
    borderRadius: theme.borderRadius.button,
    fontSize: '16px',
    fontWeight: theme.fonts.weightBold,
    fontFamily: theme.fonts.heading,
    letterSpacing: '0.5px',
    height: '40px',
    marginBottom: '8px',
  });

  const contentStyle: React.CSSProperties = {
    padding: '32px 48px',
    display: 'flex',
    gap: '32px',
    maxWidth: '1400px',
    margin: '0 auto',
  };

  const leftColumnStyle: React.CSSProperties = {
    flex: '1',
    minWidth: '820px',
    display: 'flex',
    flexDirection: 'column',
    gap: '28px',
  };

  const rightColumnStyle: React.CSSProperties = {
    width: '100%',
    maxWidth: '400px',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    borderRadius: '16px',
    padding: '28px',
  };

  const cardTitleStyle: React.CSSProperties = {
    fontSize: '20px',
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.text,
    fontFamily: theme.fonts.heading,
    letterSpacing: '0.5px',
    marginBottom: '20px',
  };

  const venueImageStyle: React.CSSProperties = {
    width: '200px',
    height: '150px',
    borderRadius: theme.borderRadius.button,
    objectFit: 'cover',
  };

  const venueRowStyle: React.CSSProperties = {
    display: 'flex',
    gap: '24px',
    marginBottom: '20px',
  };

  const infoLabelStyle: React.CSSProperties = {
    fontSize: '12px',
    color: theme.colors.textSecondary,
    marginBottom: '4px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  };

  const infoValueStyle: React.CSSProperties = {
    fontSize: '16px',
    color: theme.colors.text,
    fontWeight: theme.fonts.weightNormal,
  };

  const buttonStyle: React.CSSProperties = {
    width: '100%',
    padding: '16px',
    borderRadius: theme.borderRadius.button,
    fontSize: '16px',
    fontWeight: theme.fonts.weightBold,
    fontFamily: theme.fonts.heading,
    letterSpacing: '0.5px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    border: 'none',
    textAlign: 'center',
  };

  const primaryButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: theme.colors.primary,
    color: '#181A1B',
  };

  const secondaryButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: 'transparent',
    color: theme.colors.primary,
    border: `1px solid ${theme.colors.primary}`,
  };

  const dangerButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: 'transparent',
    color: theme.colors.danger,
    border: `1px solid ${theme.colors.danger}`,
  };

  const dividerStyle: React.CSSProperties = {
    height: '1px',
    backgroundColor: theme.colors.border,
    margin: '20px 0',
  };

  const timelineItemStyle: React.CSSProperties = {
    display: 'flex',
    gap: '16px',
    alignItems: 'flex-start',
  };

  const timelineDotStyle = (isActive: boolean): React.CSSProperties => ({
    width: '12px',
    height: '12px',
    borderRadius: '6px',
    backgroundColor: isActive ? theme.colors.primary : theme.colors.textSecondary,
    flexShrink: 0,
    marginTop: '4px',
  });

  if (loading) {
    return (
      <div style={{ ...containerStyle, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <p style={{ color: theme.colors.textSecondary }}>Cargando...</p>
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div style={{ ...containerStyle, display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', gap: '16px' }}>
        <p style={{ color: theme.colors.danger }}>{error || 'Reserva no encontrada'}</p>
        <button style={primaryButtonStyle} onClick={() => navigate('/my-bookings')}>
          Volver a Mis Reservas
        </button>
      </div>
    );
  }

  const totalHours = booking.durationHours;
  const subtotal = booking.totalAmount;

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <button style={backButtonStyle} onClick={() => navigate('/my-bookings')}>
          <ArrowLeft size={20} />
          <span>Volver a Mis Reservas</span>
        </button>

        <div style={headerContentStyle}>
          <div>
            <h1 style={titleStyle}>DETALLE DE RESERVA</h1>
            <p style={subtitleStyle}>Reserva #{booking.confirmationCode}</p>
          </div>

          <div style={{ textAlign: 'right' }}>
            <div style={statusBadgeStyle(booking.status)}>
              {getStatusLabel(booking.status)}
            </div>
            <p style={subtitleStyle}>
              Reservado el {new Date(booking.createdAt).toLocaleDateString('es-AR', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={contentStyle}>
        {/* Left Column */}
        <div style={leftColumnStyle}>
          {/* Venue Card */}
          <div style={cardStyle}>
            <h2 style={cardTitleStyle}>INFORMACIÓN DE LA CANCHA</h2>

            <div style={venueRowStyle}>
              {booking.venue.photos && booking.venue.photos.length > 0 && (
                <img
                  src={booking.venue.photos[0].url}
                  alt={booking.venue.name}
                  style={venueImageStyle}
                />
              )}

              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '12px', justifyContent: 'center' }}>
                <h3 style={{ fontSize: '24px', fontWeight: theme.fonts.weightBold, color: theme.colors.text }}>
                  {booking.venue.name}
                </h3>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: theme.colors.textSecondary }}>
                  <MapPin size={16} />
                  <span>{booking.venue.address}, {booking.venue.city}</span>
                </div>

                <div style={{ display: 'flex', gap: '16px' }}>
                  <div>
                    <p style={infoLabelStyle}>Tipo</p>
                    <p style={infoValueStyle}>{booking.venue.venueType.replace('_', ' ')}</p>
                  </div>
                  <div>
                    <p style={infoLabelStyle}>Capacidad</p>
                    <p style={infoValueStyle}>{booking.venue.capacity} jugadores</p>
                  </div>
                </div>
              </div>
            </div>

            <div style={dividerStyle} />

            <button
              style={secondaryButtonStyle}
              onClick={() => navigate(`/venues/${booking.venue.id}`)}
            >
              VER DETALLES DE LA CANCHA
            </button>
          </div>

          {/* Booking Info Card */}
          <div style={cardStyle}>
            <h2 style={cardTitleStyle}>INFORMACIÓN DE LA RESERVA</h2>

            <div style={{ display: 'flex', gap: '16px', marginTop: '20px' }}>
              <div style={{ flex: 1, backgroundColor: theme.colors.background, borderRadius: theme.borderRadius.button, padding: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', color: theme.colors.primary }}>
                  <Calendar size={20} />
                  <p style={infoLabelStyle}>FECHA</p>
                </div>
                <p style={{ ...infoValueStyle, fontSize: '18px' }}>
                  {new Date(booking.bookingDate).toLocaleDateString('es-AR', {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                  })}
                </p>
              </div>

              <div style={{ flex: 1, backgroundColor: theme.colors.background, borderRadius: theme.borderRadius.button, padding: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', color: theme.colors.primary }}>
                  <Clock size={20} />
                  <p style={infoLabelStyle}>HORARIO</p>
                </div>
                <p style={{ ...infoValueStyle, fontSize: '18px' }}>
                  {booking.startTime} - {booking.endTime}
                </p>
              </div>

              <div style={{ flex: 1, backgroundColor: theme.colors.background, borderRadius: theme.borderRadius.button, padding: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', color: theme.colors.primary }}>
                  <Clock size={20} />
                  <p style={infoLabelStyle}>DURACIÓN</p>
                </div>
                <p style={{ ...infoValueStyle, fontSize: '18px' }}>
                  {totalHours} {totalHours === 1 ? 'hora' : 'horas'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div style={rightColumnStyle}>
          {/* Payment Card */}
          <div style={cardStyle}>
            <h2 style={cardTitleStyle}>INFORMACIÓN DE PAGO</h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: theme.colors.textSecondary }}>
                  Alquiler de cancha ({totalHours}h)
                </span>
                <span style={{ color: theme.colors.text }}>{formatPrice(subtotal)}</span>
              </div>

            </div>

            <div style={dividerStyle} />

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '18px', fontWeight: theme.fonts.weightBold, color: theme.colors.text, fontFamily: theme.fonts.heading }}>
                TOTAL PAGADO:
              </span>
              <span style={{ fontSize: '28px', fontWeight: theme.fonts.weightBold, color: theme.colors.primary, fontFamily: theme.fonts.heading }}>
                {formatPrice(booking.totalAmount)}
              </span>
            </div>

            {booking.mercadopagoPayment && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                backgroundColor: theme.colors.background,
                padding: '16px',
                borderRadius: theme.borderRadius.button,
                marginTop: '20px',
              }}>
                <CreditCard size={20} color={theme.colors.primary} />
                <span style={{ color: theme.colors.text }}>
                  Mercado Pago •••• {booking.mercadopagoPayment.paymentId?.slice(-4) || '****'}
                </span>
              </div>
            )}
          </div>

          {/* Actions Card */}
          <div style={cardStyle}>
            <h2 style={cardTitleStyle}>ACCIONES</h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {booking.status === 'CONFIRMED' && (
                <>
                  <button style={secondaryButtonStyle}>
                    <Edit size={16} style={{ display: 'inline', marginRight: '8px' }} />
                    MODIFICAR RESERVA
                  </button>

                  <button style={dangerButtonStyle} onClick={handleCancelBooking}>
                    <XCircle size={16} style={{ display: 'inline', marginRight: '8px' }} />
                    CANCELAR RESERVA
                  </button>
                </>
              )}

              <button
                style={primaryButtonStyle}
                onClick={() => navigate(`/create-match?venueId=${booking.venueId}&bookingId=${booking.id}`)}
              >
                <Plus size={16} style={{ display: 'inline', marginRight: '8px' }} />
                CREAR PARTIDO
              </button>
            </div>
          </div>

          {/* Timeline Card */}
          <div style={cardStyle}>
            <h2 style={cardTitleStyle}>HISTORIAL</h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={timelineItemStyle}>
                <div style={timelineDotStyle(booking.status === 'CONFIRMED' || booking.status === 'COMPLETED')} />
                <div style={{ flex: 1 }}>
                  <p style={{ ...infoValueStyle, marginBottom: '4px' }}>Reserva confirmada</p>
                  <p style={{ ...infoLabelStyle, fontSize: '12px' }}>
                    {new Date(booking.createdAt).toLocaleString('es-AR')}
                  </p>
                </div>
              </div>

              {booking.status === 'COMPLETED' && (
                <div style={timelineItemStyle}>
                  <div style={timelineDotStyle(true)} />
                  <div style={{ flex: 1 }}>
                    <p style={{ ...infoValueStyle, marginBottom: '4px' }}>Reserva completada</p>
                    <p style={{ ...infoLabelStyle, fontSize: '12px' }}>
                      {new Date(booking.updatedAt || booking.createdAt).toLocaleString('es-AR')}
                    </p>
                  </div>
                </div>
              )}

              {booking.status === 'CANCELLED' && (
                <div style={timelineItemStyle}>
                  <div style={timelineDotStyle(false)} />
                  <div style={{ flex: 1 }}>
                    <p style={{ ...infoValueStyle, marginBottom: '4px', color: theme.colors.danger }}>
                      Reserva cancelada
                    </p>
                    <p style={{ ...infoLabelStyle, fontSize: '12px' }}>
                      {new Date(booking.updatedAt || booking.createdAt).toLocaleString('es-AR')}
                    </p>
                  </div>
                </div>
              )}

              <div style={timelineItemStyle}>
                <div style={timelineDotStyle(false)} />
                <div style={{ flex: 1 }}>
                  <p style={{ ...infoValueStyle, marginBottom: '4px', color: theme.colors.textSecondary }}>
                    Pago procesado
                  </p>
                  <p style={{ ...infoLabelStyle, fontSize: '12px' }}>
                    {new Date(booking.createdAt).toLocaleString('es-AR')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingDetailPage;
